<template>
  <div
    class="phone-input__wrapper"
    :class="{
      'phone-input__wrapper--focused': focused,
      'phone-input__wrapper--readonly': props.readonly,
    }"
  >
    <div v-if="disabled" class="phone-input__disable-mask"></div>
    <div class="phone-input__wrapper-inner">
      <input
        ref="phoneInput"
        v-model="number"
        class="phone-input"
        :class="{ 'phone-input--table-cell': props.isTableCell }"
        type="phone"
        :readonly="props.readonly"
        :autofocus="props.autofocus"
        @focus="focused = true"
        @blur="onBlur"
        @keydown.enter="onEnter"
        @keydown.stop.escape="onEscape"
      />
      <span
        v-if="status && number"
        class="phone-input__append"
        :class="{
          'phone-input__append--success': status === 'success',
          'phone-input__append--warning': status === 'warning',
          'phone-input__append--error': status === 'error',
        }"
      >
        <octicon
          v-if="status === 'error'"
          class="phone-input__append-icon"
          icon="x-circle"
        />
        <octicon
          v-else-if="status === 'success'"
          class="phone-input__append-icon"
          icon="check-circle"
        />
        <fluent-icon
          v-else-if="status === 'warning'"
          icon="warning"
          size="20"
        />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import intlTelInput from 'intl-tel-input';

interface Props {
  modelValue: string;
  status?: 'success' | 'warning' | 'error';
  disabled?: boolean;
  readonly?: boolean;
  isTableCell?: boolean;
  autofocus?: boolean;
}
interface Emits {
  (e: 'update:modelValue', value: string | null): void;
  (e: 'validation-changed', value: boolean): void;
  (e: 'blur'): void;
  (e: 'enter'): void;
  (e: 'escape'): void;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});
const emit = defineEmits<Emits>();

const phoneInput = ref<HTMLInputElement | null>(null);
const focused = ref(false);
const intl = ref<ReturnType<typeof intlTelInput> | null>(null);
const number = ref(props.modelValue || '');
const setNumberTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

function setNumber() {
  if (!intl.value) return;

  // Clear any existing timeout
  if (setNumberTimeout.value) {
    clearTimeout(setNumberTimeout.value);
    setNumberTimeout.value = null;
  }

  if (props.modelValue) {
    number.value = props.modelValue;
    intl.value.setNumber(props.modelValue);
    setNumberTimeout.value = setTimeout(() => {
      // Check if component is still mounted
      if (intl.value) {
        number.value = intl.value.getNumber(2) || ''; // national format
      }
    }, 100);
  } else {
    number.value = '';
  }
}

onMounted(async () => {
  const input = phoneInput.value as HTMLInputElement;
  if (input) {
    try {
      intl.value = intlTelInput(input, {
        utilsScript:
          'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/16.0.0/js/utils.js',
        autoPlaceholder: 'aggressive',
        dropdownContainer: document.body, // Render dropdown in body to escape modal overflow constraints
      });

      // Add script error handling
      const utilsScript = document.querySelector(
        'script[src*="intl-tel-input"][src*="utils.js"]'
      );
      if (utilsScript) {
        utilsScript.addEventListener('error', () => {
          console.warn(
            'Failed to load intl-tel-input utils script. Phone validation may be limited.'
          );
          // Continue to work without utils script - basic functionality still available
        });
      }

      input.addEventListener('countrychange', validateAndEmitValue);
      setNumber();
    } catch (error) {
      console.error('Failed to initialize phone input:', error);
      // Fallback to basic input functionality
      number.value = props.modelValue || '';
    }
  }
});

onUnmounted(() => {
  // Clear any pending timeouts
  if (setNumberTimeout.value) {
    clearTimeout(setNumberTimeout.value);
    setNumberTimeout.value = null;
  }

  if (intl.value) {
    // Remove event listener
    phoneInput.value?.removeEventListener(
      'countrychange',
      validateAndEmitValue
    );

    // Destroy intl-tel-input instance immediately to prevent memory leaks
    intl.value.destroy();
    intl.value = null;
  }
});

function getNumber() {
  if (intl.value) {
    return intl.value.getNumber();
  }
  return null;
}

function isValidNumber() {
  if (intl.value) {
    return intl.value.isValidNumber();
  }
  return false;
}

function validateAndEmitValue() {
  if (isValidNumber()) {
    emit('update:modelValue', getNumber());
    emit('validation-changed', true);
    number.value = intl.value?.getNumber(2) || '';
  } else {
    emit('update:modelValue', number.value);
    emit('validation-changed', false);
  }
}

// update internal state when modelValue is changed by parent
watch(
  () => props.modelValue,
  () => {
    const internalValue = getNumber();
    if (internalValue === props.modelValue) return;
    setNumber();
  }
);
watch(number, validateAndEmitValue);

function onBlur() {
  focused.value = false;
  emit('blur');
}

function onEnter() {
  emit('enter');
}

function onEscape() {
  emit('escape');
}

defineExpose({
  phoneInput,
  focus: () => phoneInput.value?.focus(),
});
</script>

<style lang="scss" scoped>
.phone-input {
  @apply tw-rounded tw-outline-none tw-text-xs tw-text-gray-medium-dark tw-leading-8 tw-flex-1;
  padding-right: 0px;
  max-width: calc(100% - 1.5rem);

  &--table-cell {
    @apply tw-leading-none;
    padding-top: 0;
    padding-bottom: 0;
  }

  &__wrapper {
    @apply tw-rounded-md tw-relative;

    :deep(.iti.iti--allow-dropdown) {
      width: 98%;
    }

    &-inner {
      @apply tw-border tw-border-gray-medium tw-rounded tw-flex tw-items-center tw-bg-white;
      width: 100%;
    }

    &:not(&--focused):not(&--readonly) &-inner:hover {
      @apply tw-border-gray-dark;
    }

    &--focused &-inner {
      border-color: var(--q-primary, var(--primary-color)) !important;
    }

    &--readonly &-inner {
      @apply tw-border-dashed tw-pointer-events-none;
    }
  }

  &__disable-mask {
    @apply tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-cursor-not-allowed tw-z-30 tw-bg-white tw-opacity-40;
    bottom: -5px;
  }

  &::placeholder {
    @apply tw-text-gray-medium tw-text-xs;
  }

  &__append {
    @apply tw-text-xs;
    margin-left: -1.2rem;
    margin-top: 0.25rem;

    &-icon {
      @apply tw-h-4 tw-w-4 tw-shrink-0;

      :deep(svg) {
        height: 16px !important;
      }
    }

    &--success {
      @apply tw-text-green;
    }

    &--error {
      @apply tw-text-red;
    }

    &--warning {
      @apply tw-text-yellow;
      margin-left: -1.4rem;
      margin-top: 0;
    }
  }
}
</style>
